/**
 * FlujoEfectivo.js — Estado de Flujo de Efectivo por el método directo (NIC 7).
 *
 * Solo genera PDF: no hay vista en pantalla porque el estado se lee como
 * documento, no como tabla filtrable. Las opciones (moneda, idioma, firma,
 * comparativo y segundo firmante) las pide `usePDFIndividual`, que es el mismo
 * modal que usan el Estado de Resultados y el Balance General — así las cinco
 * opciones se comportan igual en todos los reportes del sistema.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import { usePDFIndividual } from "./EstadoFinanciero";

export default function FlujoEfectivo() {
  const { t, i18n } = useTranslation();

  const now       = new Date();
  const anoInicio = `${now.getFullYear()}-01-01`;
  const hoy       = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  const [fechaDesde, setFechaDesde] = useState(anoInicio);
  const [fechaHasta, setFechaHasta] = useState(hoy);

  const { loading, error, generar, modal } = usePDFIndividual(
    "/api/finance/reportes/flujo-efectivo-pdf/",
    `flujo_efectivo_${fechaHasta.replace(/-/g, "")}.pdf`,
    { showComparativo: true },
  );

  const fmtLindo = (iso) => {
    if (!iso) return "-";
    const date   = new Date(iso + "T00:00:00");
    const locale = i18n.resolvedLanguage === "en" ? "en-US" : "es-CR";
    return date.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
  };

  const SECCIONES = [
    { icon: "fa-arrow-right-arrow-left", n: "01", labelKey: "reportes.flujoSec01Label", descKey: "reportes.flujoSec01Desc", color: "#f97316" },
    { icon: "fa-building-flag",          n: "02", labelKey: "reportes.flujoSec02Label", descKey: "reportes.flujoSec02Desc", color: "#60a5fa" },
    { icon: "fa-landmark",               n: "03", labelKey: "reportes.flujoSec03Label", descKey: "reportes.flujoSec03Desc", color: "#a78bfa" },
    { icon: "fa-scale-balanced",         n: "04", labelKey: "reportes.flujoSec04Label", descKey: "reportes.flujoSec04Desc", color: "#4ade80" },
  ];

  function handleGenerar() {
    const qs = new URLSearchParams();
    if (fechaDesde) qs.set("fecha_desde", fechaDesde);
    if (fechaHasta) qs.set("fecha_hasta", fechaHasta);
    generar(qs.toString());
  }

  return (
    <>
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("reportes.flujoEfectivo")}</span>
      </span>

      <div>
        <h2 className="title mt-2 mb-2">{t("reportes.flujoEfectivo")}</h2>
        <p className="mb-3" style={{ color: "var(--text-muted)" }}>
          {t("reportes.flujoEfectivoDesc")}
        </p>
      </div>

      {/* ── Selector de período ── */}
      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 12, padding: "22px 24px", marginBottom: 24 }}>

        {error && (
          <div style={{ background: "rgba(248,113,113,0.08)",
            border: "1px solid rgba(248,113,113,0.25)", borderLeft: "3px solid #f87171",
            borderRadius: 8, padding: "10px 14px", marginBottom: 18,
            color: "#fca5a5", fontSize: "0.83rem",
            display: "flex", alignItems: "center", gap: 8 }}>
            <i className="fa-solid fa-circle-exclamation" />
            {error}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16, alignItems: "flex-end" }}>
          <div>
            <label style={{ display: "block", fontSize: "0.68rem", fontWeight: 700,
              letterSpacing: "1px", textTransform: "uppercase",
              color: "var(--text-muted)", marginBottom: 8 }}>
              {t("reportes.desde")}
            </label>
            <div style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)",
              borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
              <i className="fa-regular fa-calendar" style={{ color: "#f97316", fontSize: "0.88rem", flexShrink: 0 }} />
              <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
                style={{ background: "transparent", border: "none", outline: "none",
                  color: "var(--text-primary)", fontSize: "0.88rem", fontWeight: 600,
                  colorScheme: "light dark", cursor: "pointer", flex: 1, width: "100%" }} />
            </div>
            <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 4, paddingLeft: 2 }}>
              {fmtLindo(fechaDesde)}
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: "0.68rem", fontWeight: 700,
              letterSpacing: "1px", textTransform: "uppercase",
              color: "var(--text-muted)", marginBottom: 8 }}>
              {t("reportes.hasta")}
            </label>
            <div style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)",
              borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
              <i className="fa-regular fa-calendar-check" style={{ color: "#4ade80", fontSize: "0.88rem", flexShrink: 0 }} />
              <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
                style={{ background: "transparent", border: "none", outline: "none",
                  color: "var(--text-primary)", fontSize: "0.88rem", fontWeight: 600,
                  colorScheme: "light dark", cursor: "pointer", flex: 1, width: "100%" }} />
            </div>
            <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 4, paddingLeft: 2 }}>
              {fmtLindo(fechaHasta)}
            </div>
          </div>
        </div>

        {fechaDesde && fechaHasta && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border-card)",
            display: "flex", alignItems: "center", gap: 8 }}>
            <i className="fa-solid fa-circle-info" style={{ color: "var(--text-muted)", fontSize: "0.78rem" }} />
            <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
              {t("reportes.periodo")}{" "}
              <strong style={{ color: "var(--text-secondary)" }}>
                {fmtLindo(fechaDesde)} - {fmtLindo(fechaHasta)}
              </strong>
            </span>
          </div>
        )}
      </div>

      {/* ── Contenido del PDF ── */}
      <div style={{ marginBottom: 8 }}>
        <p style={{ margin: "0 0 16px", fontSize: "0.7rem", fontWeight: 700,
          letterSpacing: "2px", textTransform: "uppercase",
          color: "var(--text-muted)", textAlign: "center" }}>
          {t("reportes.contenidoPDF")}
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
          {SECCIONES.map(({ icon, n, labelKey, descKey, color }) => (
            <div key={n}
              style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
                borderRadius: 12, padding: "18px 20px",
                display: "flex", alignItems: "center", gap: 14,
                position: "relative", overflow: "hidden",
                transition: "border-color 0.15s, transform 0.1s" }}
              onMouseOver={e => {
                e.currentTarget.style.borderColor = color + "55";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseOut={e => {
                e.currentTarget.style.borderColor = "var(--border-card)";
                e.currentTarget.style.transform = "translateY(0)";
              }}>
              <span style={{ position: "absolute", right: 12, top: "50%",
                transform: "translateY(-50%)", fontSize: "3.8rem", fontWeight: 900,
                fontFamily: "'Montserrat',sans-serif", color: color + "12",
                lineHeight: 1, userSelect: "none", letterSpacing: "-2px",
                pointerEvents: "none" }}>
                {n}
              </span>
              <div style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0,
                background: color + "14", border: `1px solid ${color}28`,
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className={`fa-solid ${icon}`} style={{ color, fontSize: "1rem" }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "0.87rem", fontWeight: 700,
                  color: "var(--text-primary)", lineHeight: 1.3, marginBottom: 3 }}>
                  {t(labelKey)}
                </div>
                <div style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>{t(descKey)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cómo se arma — el contador necesita saber de dónde salen las partidas */}
      <div style={{ marginTop: 18, background: "var(--hover-bg)",
        border: "1px solid var(--border-card)", borderRadius: 10,
        padding: "13px 16px", display: "flex", gap: 11, alignItems: "flex-start" }}>
        <i className="fa-solid fa-circle-info"
           style={{ color: "#60a5fa", fontSize: "0.85rem", marginTop: 2, flexShrink: 0 }} />
        <span style={{ fontSize: "0.78rem", color: "var(--text-muted)", lineHeight: 1.55 }}>
          {t("reportes.flujoNotaMetodo")}
        </span>
      </div>

      {/* ── Botón principal ── */}
      <div style={{ marginTop: 24, display: "flex", justifyContent: "center" }}>
        <button onClick={handleGenerar} disabled={loading}
          style={{ background: "#f97316", border: "none", borderRadius: 10,
            padding: "13px 40px", color: "#fff", fontWeight: 700, fontSize: "1rem",
            cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1,
            display: "flex", alignItems: "center", gap: 10,
            boxShadow: "0 8px 28px rgba(249,115,22,0.32)", transition: "all 0.15s" }}
          onMouseOver={e => { if (!loading) { e.currentTarget.style.background = "#ea6b10"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
          onMouseOut={e  => { if (!loading) { e.currentTarget.style.background = "#f97316"; e.currentTarget.style.transform = "translateY(0)"; } }}>
          {loading
            ? <><i className="fa-solid fa-circle-notch fa-spin" /> {t("reportes.generandoPDF")}</>
            : <><i className="fa-solid fa-file-pdf" style={{ fontSize: "1.1rem" }} /> {t("reportes.generarPDF")}</>}
        </button>
      </div>

      <p style={{ textAlign: "center", marginTop: 12, fontSize: "0.74rem", color: "var(--text-muted)" }}>
        {t("reportes.descargaAuto")}
      </p>

      {modal}
    </>
  );
}
